<template>
  <div class="top-picks">
    <div class="flex justify-between align-center">
      <div class="top-picks-title">{{$t('Top Picks for Travelers')}}</div>
      <div class="slider-navigation">
        <div class="slider-navigation__btn" @click="scroll('left')">
          <span class="icon icon-chevron-left"></span>
        </div>
        <div class="slider-navigation__btn" @click="scroll('right')">
          <span class="icon icon-chevron-right"></span>
        </div>
      </div>
    </div>
    <div class="top-picks--list flex overflow-x-auto scroll-smooth snap-x snap-mandatory pb-4" ref="scrollContainer" v-if="data?.activities">
      <a
        v-for="activity in data?.activities"
        :key="activity.id"
        :href="`/experiences/${city_slug}/${activity.id}-${activity.slug}`"
        class="top-picks--item"
      >
        <div class="top-picks--image">
          <img v-if="imageUrlWithProxy(activity.cover_image_url, 375, 218)" :src="imageUrlWithProxy(activity.cover_image_url, 357, 218)" :alt="activity.name">
          <PhotoPlaceholder v-else :font-size="'42px'"/>
        </div>
        <div class="top-picks--content">
          <div class="top-picks--title">{{activity.name}}</div>
          <div class="top-picks--location">
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path fill-rule="evenodd" clip-rule="evenodd" d="M9 16.4248L9.59737 17.0072C9.51898 17.0842 9.42582 17.1452 9.32324 17.1869C9.22065 17.2286 9.11066 17.25 8.99958 17.25C8.8885 17.25 8.77851 17.2286 8.67592 17.1869C8.57334 17.1452 8.48017 17.0842 8.40178 17.0072L8.39672 17.0014L8.38237 16.9874L8.33006 16.9354C8.03263 16.6359 7.74009 16.3317 7.45256 16.023C6.73065 15.2496 6.03939 14.4493 5.38031 13.6239C4.62769 12.6752 3.85819 11.6027 3.27347 10.5434C2.70141 9.50477 2.25 8.37949 2.25 7.3499C2.25 3.60528 5.28075 0.75 9 0.75C12.7192 0.75 15.75 3.60528 15.75 7.3499C15.75 8.37949 15.2986 9.50477 14.7265 10.5426C14.1418 11.6035 13.3732 12.6752 12.6197 13.6239C11.6978 14.7786 10.7131 15.884 9.66994 16.9354L9.61763 16.9874L9.60328 17.0014L9.59822 17.0064L9 16.4248ZM9 9.82487C9.67133 9.82487 10.3152 9.56411 10.7899 9.09997C11.2646 8.63582 11.5312 8.00631 11.5312 7.3499C11.5312 6.6935 11.2646 6.06399 10.7899 5.59984C10.3152 5.13569 9.67133 4.87494 9 4.87494C8.32867 4.87494 7.68484 5.13569 7.21014 5.59984C6.73543 6.06399 6.46875 6.6935 6.46875 7.3499C6.46875 8.00631 6.73543 8.63582 7.21014 9.09997C7.68484 9.56411 8.32867 9.82487 9 9.82487Z" fill="#292929"/>
            </svg>
            <span>{{activity.location.city_name}}, {{activity.location.country_name}}</span>
          </div>
          <div class="top-picks--duration" v-if="false">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <g clip-path="url(#clip0_10381_4733)">
                <path d="M8.00527 4.00016V8.0035L10.8316 10.8302M8.0026 14.6668C11.6846 14.6668 14.6693 11.6822 14.6693 8.00016C14.6693 4.31816 11.6846 1.3335 8.0026 1.3335C4.3206 1.3335 1.33594 4.31816 1.33594 8.00016C1.33594 11.6822 4.3206 14.6668 8.0026 14.6668Z" stroke="#292929" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              </g>
              <defs>
                <clipPath id="clip0_10381_4733">
                  <rect width="16" height="16" fill="white"/>
                </clipPath>
              </defs>
            </svg>
            {{duration(activity.duration)}}
          </div>
          <div class="top-picks--rate">
            <Rating v-model="activity.avg_rating" disabled :cancel="false" readonly>
              <template #officon>
                <span/>
              </template>
            </Rating>
            {{activity.avg_rating}}
          </div>
          <div class="top-picks--description">{{activity.description}}</div>
          <div class="top-picks--price" v-if="activity.price_from">Price from <strong>{{activity.price_from.formatted}}</strong> per person</div>
        </div>
      </a>
    </div>
  </div>
</template>

<script setup lang="ts">
import Rating from 'primevue/rating'
import { imageUrlWithProxy } from '@/utils/imageproxy'
import dayjs from '~/utils/dayjs';
import { ref } from "vue";
const api = useApi()
const scrollContainer = ref<HTMLElement | null>(null)

const data = ref<any>(null)

function duration(time: number) {
  return dayjs.duration(time).humanize()
}

const { data: fetchedData } = await useAsyncData('get-activities', fetchData)
if (fetchedData.value) {
  data.value = fetchedData.value
}

const scroll = (direction:string) => {
  const container = scrollContainer.value
  const scrollAmount = 357 + 24
  container?.scrollBy({
    left: direction === 'left' ? -scrollAmount : scrollAmount,
    behavior: 'smooth',
  })
}

const place_id = 192;
const city_slug = 'us-fl-miami';

async function fetchData() {
  try {
    const paramsAttractions = {
      place_id: place_id,
      size: 6,
      sort_by: 'rating_desc'
    }
    return await api('/api/product/v3/activities', {params: paramsAttractions})
  } catch (error) {
    console.log(error)
    return null
  }
}
</script>


<style lang="scss">
.top-picks-title {
  font-size: 24px;
  font-weight: 600;
  line-height: 36px;
  margin-bottom: 20px;
}
.top-picks--list {
  display: flex;
  justify-content: space-between;
  gap: 24px;
}

.top-picks {
  &--item {
    min-width: 357px;
    width: 100%;
    border: 2px solid var(--grey-border);
    border-radius: 10px;
  }

  &--image {
    border-radius: 8px;
    margin: -2px;
    overflow: hidden;
    height: 215px;

    img {
      width: 100%;
    }
  }

  &--content {
    padding: 16px;
    display: flex;
    flex-direction: column;
    gap: 12px;
    color: var(--dark-grey);

  }

  &--title {
    color: var(--black);
    font-size: 16px;
    line-height: 24px;
    font-weight: 600;
    text-overflow: ellipsis;
    max-height: 24px;
    -webkit-line-clamp: 1;
    -webkit-box-orient: vertical;
    display: -webkit-box;
    word-break: break-word;
    overflow: hidden;
  }

  &--location {
    display: flex;
    align-items: center;
    gap: 5px;
    font-size: 14px;
  }

  &--description {
    font-size: 14px;
    overflow: hidden;
    text-overflow: ellipsis;
    max-height: 72px;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    display: -webkit-box;
    word-break: break-word;
    box-sizing: content-box;
    min-height: 20px;
  }

  &--duration {
    font-size: 14px;
    display: flex;
    gap: 5px;
    align-items: center;
    width: fit-content;
    border: 1px solid var(--dark-grey);
    border-radius: 10px;
    padding: 4px 12px;
  }

  &--price {
    border-top: 2px solid var(--grey-border);
    padding-top: 12px;
    text-align: right;
    strong {
      color: var(--black);
    }
  }

  &--rate {
    display: flex;
    gap: 5px;
    align-items: center;
    font-size: 14px;
  }
}

.slider-navigation {
  display: flex;
  justify-content: space-between;
  gap: 8px;

  &__btn {
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 18px;
    width: 32px;
    height: 32px;
    background: var(--blue-highlight);
    color: var(--blue);
    border-radius: 10px;
    cursor: pointer;

    &:hover {
      background: var(--blue);
      color: #fff;
    }

    .disabled {
      opacity: 0.4;
    }
  }
}

/* Optional: hide scrollbar */
::-webkit-scrollbar {
  display: none;
}
</style>
